import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const RoleOffice = () => {
  const [roles, setRoles] = useState([]);
  const [workLocation, setWorkLocation] = useState([]);
  const [units, setUnits] = useState([]);
  const [currentUser, setCurrentUser] = useState({}); 
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLocationTerm, setSearchLocationTerm] = useState('');
  const [searchUnitTerm, setSearchUnitTerm] = useState('');
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('roles');
  const [roleError, setRoleError] = useState('');
  const [roleState, setRoleState] = useState([]);

  const currentUserId = localStorage.getItem('id');
  const navigate = useNavigate();

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get('/role/')
      .then(res => setRoles(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get('/office-branch/')
      .then(res => setWorkLocation(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get('/branch-unit/')
      .then(res => setUnits(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/user/${currentUserId}/`)
      .then(res => {
        const userData = res.data;
        setCurrentUser({ 
          ...userData, 
          role: userData.role?.name || userData.role?.[0]?.name || 'guest' 
        });
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRoleState([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: roleState[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const filteredRoles = roles.filter(role => 
    role.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLocations = workLocation.filter(location => 
    location.name.toLowerCase().includes(searchLocationTerm.toLowerCase())
  );

  const filteredUnits = units.filter(unit => 
    unit.name.toLowerCase().includes(searchUnitTerm.toLowerCase())
  );

  const handleLocationClick = (lat, lng) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const handleOfficeStatusUpdate = (officeId, newStatus) => {
    axiosInstance.patch(`/office-branch/${officeId}/`, { status: newStatus })
      .then(() => {
        setWorkLocation(workLocation.map(loc => 
          loc.id === officeId ? { ...loc, status: newStatus } : loc
        ));
      })
      .catch(console.error);
  };

  const handleLogout = () => {
    axiosInstance.post('/logout/', {}).finally(() => {
      localStorage.clear();
      window.location.href = '/';
    });
  };

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>🏢 Roles & Office</h1>
          <p>Manage roles, office branches, and units</p>
        </div>

        <div className="tab-navigation">
          <button 
            className={`tab-nav-btn ${activeTab === 'roles' ? 'active' : ''}`}
            onClick={() => setActiveTab('roles')}
          >
            🎭 Roles
          </button>
          <button 
            className={`tab-nav-btn ${activeTab === 'locations' ? 'active' : ''}`}
            onClick={() => setActiveTab('locations')}
          >
            📍 Office Branches
          </button>
          <button 
            className={`tab-nav-btn ${activeTab === 'units' ? 'active' : ''}`}
            onClick={() => setActiveTab('units')}
          >
            🏬 Units
          </button>
        </div>

        {activeTab === 'roles' && (
          <div className="dashboard-card">
            <div className="card-header">
              <span className="card-icon">🎭</span>
              <h3>Roles</h3>
              <Link to="/create-role-office" state={{ form: 'role' }} className="add-btn">
                ➕ Add Role
              </Link>
            </div>
            <div className="card-body">
              <div className="filter-bar">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Role Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoles.map(role => (
                      <tr key={role.id}>
                        <td>
                          <span className="role-badge">{role.name}</span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <Link to={`/update-roles/${role.id}`} className="icon-btn" title="Edit">
                              ✏️
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'locations' && (
          <div className="dashboard-card">
            <div className="card-header">
              <span className="card-icon">📍</span>
              <h3>Office Branches</h3>
              <Link to="/create-office" state={{ form: 'location' }} className="add-btn">
                ➕ Add Location
              </Link>
            </div>
            <div className="card-body">
              <div className="filter-bar">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search locations..."
                  value={searchLocationTerm}
                  onChange={(e) => setSearchLocationTerm(e.target.value)}
                />
              </div>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Name</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLocations.map(location => (
                      <tr key={location.id}>
                        <td>{location.unit_name || '—'}</td>
                        <td>{location.name}</td>
                        <td>
                          <button 
                            className="icon-btn" 
                            title="View on Map"
                            onClick={() => handleLocationClick(location.latitude, location.longitude)}
                          >
                            📍
                          </button>
                        </td>
                        <td>
                          <span className={`status-badge ${location.status === 'active' ? 'approved' : 'rejected'}`}>
                            {location.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <Link to={`/update-location/${location.id}`} className="icon-btn" title="Edit">
                              ✏️
                            </Link>
                            <button 
                              className="icon-btn" 
                              title={location.status === 'active' ? 'Deactivate' : 'Activate'}
                              onClick={() => handleOfficeStatusUpdate(location.id, location.status === 'active' ? 'inactive' : 'active')}
                            >
                              {location.status === 'active' ? '🔒' : '🔓'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'units' && (
          <div className="dashboard-card">
            <div className="card-header">
              <span className="card-icon">🏬</span>
              <h3>Units</h3>
              <Link to="/create-office" state={{ form: 'unit' }} className="add-btn">
                ➕ Add Unit
              </Link>
            </div>
            <div className="card-body">
              <div className="filter-bar">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search units..."
                  value={searchUnitTerm}
                  onChange={(e) => setSearchUnitTerm(e.target.value)}
                />
              </div>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Unit Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUnits.map(unit => (
                      <tr key={unit.id}>
                        <td>{unit.name}</td>
                        <td>
                          <div className="action-buttons">
                            <Link to={`/update-unit/${unit.id}`} className="icon-btn" title="Edit">
                              ✏️
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RoleOffice;
